import { Check, Sparkles, ArrowRight, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { packages, additionalServices, businessInfo } from '../data/mock';

export const PackagesSection = () => {
  return (
    <section id="packages" className="relative py-28 bg-zinc-900/30">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#50ced4]/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#50ced4] text-sm font-medium tracking-widest uppercase mb-4 block">Pricing</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Simple, Transparent{' '}
            <span className="text-[#50ced4]">Packages</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Choose the perfect package for your event. All packages include delivery and setup within the Wyndham area.
          </p>
        </div>

        {/* Packages grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative group rounded-2xl p-7 border transition-all duration-500 hover:-translate-y-1 ${
                pkg.popular
                  ? 'bg-zinc-900 border-[#50ced4]/40 shadow-lg shadow-[#50ced4]/5'
                  : 'bg-zinc-900/50 border-white/5 hover:border-[#50ced4]/20'
              }`}
            >
              {/* Popular badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-7">
                  <Badge className="bg-[#50ced4] text-zinc-950 font-semibold text-xs px-3 py-1 rounded-full hover:bg-[#50ced4]">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {pkg.tag}
                  </Badge>
                </div>
              )}

              {/* Tag (non-popular) */}
              {!pkg.popular && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-xs mb-4 rounded-full">
                  {pkg.tag}
                </Badge>
              )}

              {/* Package name */}
              <h3 className={`text-xl font-bold mb-2 ${pkg.popular ? 'text-white mt-3' : 'text-white mt-1'}`}>
                {pkg.name}
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold text-white">${pkg.price}</span>
                <span className="text-zinc-500 text-sm">/hire</span>
              </div>

              {/* Items */}
              <ul className="space-y-3 mb-8">
                {pkg.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <Check className="w-4 h-4 text-[#50ced4] mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a href={`tel:${businessInfo.contact.phones[0].replace(/\s/g, '')}`} className="block">
                <Button
                  className={`w-full rounded-xl py-5 font-semibold text-sm ${
                    pkg.popular
                      ? 'bg-[#50ced4] hover:bg-[#3eb8be] text-zinc-950'
                      : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  }`}
                >
                  Book This Package
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          ))}
        </div>

        {/* Additional Services */}
        <div className="mt-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Info className="w-4 h-4 text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Additional Services</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {additionalServices.map((service, idx) => (
              <div
                key={idx}
                className="p-6 rounded-xl bg-zinc-900/50 border border-white/5 hover:border-amber-500/20 transition-all duration-300"
              >
                <h4 className="text-white font-semibold mb-2">{service.title}</h4>
                <p className="text-zinc-500 text-sm leading-relaxed mb-3">{service.description}</p>
                <span className="text-xs text-amber-400 font-medium">{service.note}</span>
              </div>
            ))}
          </div>

          {/* Delivery note */}
          <div className="mt-8 p-5 rounded-xl bg-zinc-900/30 border border-white/5 text-center">
            <p className="text-zinc-400 text-sm">
              Delivery charges extra for suburbs outside of Wyndham Area. Contact us for a quote.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
